/**
 * ProtectedRoute Component
 * Route guard that checks authentication and permissions
 * 
 * ARCHITECTURAL STANDARD: Route protection pattern
 * All protected routes should use this wrapper
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useAuth } from '../hooks/useAuth';

/**
 * ProtectedRoute
 * Wraps routes that require authentication and/or specific permissions
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Route component(s) to render
 * @param {string|Array<string>} props.requiredPermission - Required permission(s)
 * @returns {React.ReactNode}
 */
const ProtectedRoute = ({ children, requiredPermission = null }) => {
  const { isAuthenticated, isLoading, hasAnyPermission } = useAuth();
  const location = useLocation();
  const theme = useTheme();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, color: theme.palette.text.secondary }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted location so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions if required
  if (requiredPermission) {
    const permissions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    
    if (!hasAnyPermission(permissions)) {
      // User is authenticated but doesn't have required permission
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // User is authenticated and has required permissions
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredPermission: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

export default ProtectedRoute;
